import { Component, input, OnInit, output, signal } from '@angular/core';
import { Skeleton } from '@ntv360/component-pantry';
import { Stats, StatsData } from '@shared/interfaces';
import { ColorClassPipe } from '@shared/pipes';

@Component({
    selector: 'app-stats-card',
    standalone: true,
    imports: [ColorClassPipe, Skeleton],
    templateUrl: './stats-card.component.html',
    styleUrl: './stats-card.component.scss',
    host: {
        '[style.width]': 'width()',
        '[class.stats-card--clickable]': 'clickable()',
        '(click)': 'onCardClick()',
    },
})
export class StatsCardComponent implements OnInit {
    /**
     * The list of stats to display on the card.
     */
    public statsData = input<StatsData>();

    /**
     * Card variant. Eg the dark mode one or the white one. Defaults to `white`
     */
    public variant = input<'dark' | 'light'>('light');

    /**
     * Whether to show the total highlight in the upper right or bottom-left.
     */
    public hasTotal = input<boolean>(false);

    /**
     * Whether the total position is at the top-right or at the bottom-left
     */
    public totalPosition = input<'top-right' | 'bottom-left'>('top-right');

    /**
     * Whether the card is clickable.
     */
    public clickable = input<boolean>(false);

    /**
     * Emitted when the card is clicked.
     */
    public cardClick = output<StatsData | undefined>();

    /**
     * The CSS width of the card component.
     */
    public width = input<string>('100%');

    /**
     * The CSS height of the card component.
     */
    public height = input<string>('auto');

    /**
     * Flag whether card data is loading
     */
    public isLoading = input<boolean>(false);

    /**
     * For stat data (single data) variant. Whether or not reverse the label and value position. Defaults to `false`
     */
    public reverseStatPosition = signal<boolean>(false);

    /**
     * Path to the SVG sprite file.
     */
    public spritePath = 'assets/icons/icons-sprite.svg';

    /**
     * Change the value of reverseStatPosition when totalPoistion is `bottom-left`.
     */
    public ngOnInit(): void {
        if (this.totalPosition() === 'bottom-left') {
            this.reverseStatPosition.set(true);
        }
    }

    /**
     * Handles the click event on the host element.
     */
    public onCardClick(): void {
        if (this.clickable()) {
            this.cardClick.emit(this.statsData());
        }
    }

    /**
     * Formats the value as a percentage string.
     */
    protected formatValue(value: number | string): string {
        // Return absolute value so the icon handles the direction meaning
        return `${Math.abs(Number(value))}%`;
    }

    /**
     * Checks if colorClass property for a stat data exists
     */
    protected hasColorClass(stat: Stats): boolean {
        return stat.trend?.colorClass !== undefined;
    }
}
